/**
 * CopilotChat — Floating chat widget for the QFinPricer copilot.
 * FAB button (bottom-right) opens a 400x500 dark chat panel with
 * user/bot bubbles, typing indicator, auto-scroll, and suggestion chips.
 */
import React, { useState, useRef, useEffect, useCallback } from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Fade from '@mui/material/Fade'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import SendIcon from '@mui/icons-material/Send'
import CloseIcon from '@mui/icons-material/Close'
import { keyframes } from '@mui/system'
import { useTheme } from '@mui/material/styles'
import { useCopilot } from './CopilotProvider'
import ResponseFormatter from './ResponseFormatter'

const bounce = keyframes`
  0%, 80%, 100% { transform: scale(0); }
  40% { transform: scale(1); }
`

const TypingDots: React.FC = () => {
  const theme = useTheme()
  return (
    <Box sx={{ display: 'flex', gap: 0.5, py: 0.5, px: 0.25 }}>
      {[0, 1, 2].map(i => (
        <Box
          key={i}
          sx={{
            width: 6, height: 6, borderRadius: '50%',
            backgroundColor: theme.palette.primary.light,
            animation: `${bounce} 1.4s infinite ease-in-out both`,
            animationDelay: `${i * 0.16}s`,
          }}
        />
      ))}
    </Box>
  )
}

const CopilotChat: React.FC = () => {
  const theme = useTheme()
  const { messages, loading, sendMessage } = useCopilot()
  const [open, setOpen] = useState(false)
  const [input, setInput] = useState('')
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight
  }, [messages, loading])

  const handleSend = useCallback(() => {
    const text = input.trim()
    if (!text || loading) return
    setInput('')
    sendMessage(text)
  }, [input, loading, sendMessage])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() }
  }

  const handleSuggestion = useCallback(
    (text: string) => { if (!loading) sendMessage(text) },
    [loading, sendMessage],
  )

  // Floating button (collapsed state)
  if (!open) {
    return (
      <IconButton
        onClick={() => setOpen(true)}
        sx={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 1400,
          width: 56, height: 56,
          backgroundColor: theme.palette.primary.light, color: theme.palette.primary.contrastText,
          boxShadow: `0 4px 20px ${theme.palette.primary.light}59`,
          '&:hover': { backgroundColor: theme.palette.primary.main, boxShadow: `0 6px 24px ${theme.palette.primary.main}80` },
        }}
      >
        <SmartToyIcon />
      </IconButton>
    )
  }

  // Chat panel (expanded state)
  return (
    <Fade in>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 1400,
          width: 400, height: 500,
          display: 'flex', flexDirection: 'column',
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '12px', overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2, py: 1.25,
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.default',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SmartToyIcon sx={{ color: 'primary.light', fontSize: 20 }} />
            <Typography variant="subtitle2" sx={{ color: 'text.primary', fontWeight: 600 }}>
              QFinPricer Copilot
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: 'text.secondary' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Messages area */}
        <Box
          ref={scrollRef}
          sx={{
            flex: 1, overflowY: 'auto', px: 2, py: 1.5,
            backgroundColor: 'background.default',
            display: 'flex', flexDirection: 'column', gap: 1.5,
          }}
        >
          {messages.length === 0 && !loading && (
            <Box sx={{ textAlign: 'center', mt: 6 }}>
              <SmartToyIcon sx={{ fontSize: 40, color: 'action.disabled', mb: 1 }} />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Ask me anything about your platforms.
              </Typography>
            </Box>
          )}

          {messages.map((msg, idx) => {
            const isUser = msg.role === 'user'
            return (
              <Box key={idx} sx={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start' }}>
                <Box sx={{
                  maxWidth: '85%', px: 1.5, py: 1,
                  borderRadius: isUser ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                  backgroundColor: isUser ? 'action.selected' : 'action.hover',
                  border: '1px solid',
                  borderColor: 'divider',
                }}>
                  {isUser ? (
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>{msg.text}</Typography>
                  ) : (
                    <ResponseFormatter message={msg} onSuggestionClick={handleSuggestion} />
                  )}
                </Box>
              </Box>
            )
          })}

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <Box sx={{
                px: 1.5, py: 1, borderRadius: '12px 12px 12px 2px',
                backgroundColor: 'action.hover',
                border: '1px solid',
                borderColor: 'divider',
              }}>
                <TypingDots />
              </Box>
            </Box>
          )}
        </Box>

        {/* Input bar */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          px: 1.5, py: 1,
          borderTop: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
        }}>
          <TextField
            fullWidth size="small"
            placeholder="Type a message..."
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            autoComplete="off"
            sx={{
              '& .MuiOutlinedInput-root': {
                color: 'text.primary', backgroundColor: 'background.default',
                borderRadius: '8px', fontSize: '0.85rem',
                '& fieldset': { borderColor: 'divider' },
                '&:hover fieldset': { borderColor: 'primary.light' },
                '&.Mui-focused fieldset': { borderColor: 'primary.main', borderWidth: 1 },
              },
            }}
          />
          <IconButton
            onClick={handleSend}
            disabled={!input.trim() || loading}
            sx={{ color: 'primary.light', '&.Mui-disabled': { color: 'action.disabled' } }}
          >
            <SendIcon fontSize="small" />
          </IconButton>
        </Box>
      </Paper>
    </Fade>
  )
}

export default CopilotChat
